import { Component, HostListener, OnInit } from '@angular/core';
import { ThemeService } from '../../service/theme.service';
import { IotService } from '../../service/iot.service';
import { ClientService } from '../../service/client.service';
import { ReportsService } from '../../service/reports.service';
import { IotDevicesViewService } from '../iot-devices-view.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-iot-shadow-feed',
  templateUrl: './iot-shadow-feed.component.html',
  styleUrls: ['./iot-shadow-feed.component.css'],
  standalone: false
})
export class IotShadowFeedComponent implements OnInit {
  // Shadow feed state
  allThingsShadowFeed = false;
  deviceFilterShadowFeed = '';
  thingsShadowLog: any[] = [];
  selectedThingsShadowFeed: string[] = [];
  startDateShadowFeed: any;
  endDateShadowFeed: any;
  selectedClient: string = '';
  thingClient: any[] = [];
  things: any[] = [];
  clientSubGroups: any[] = [];
  filterThingGroup: string = '';
  filterThingSubGroup: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  sortBy: string = 'thing_name';
  itemsPerPageShadowFeed = 20;
  loadedItemsShadowFeed = 0;
  loading = false;
  loadingThings = true;
  showScrollTop = false;

  constructor(
    public theme: ThemeService,
    public iotService: IotService,
    private clientService: ClientService,
    private reports: ReportsService,
    public view: IotDevicesViewService
  ) {
    this.startDateShadowFeed = this.reports.getDateOneYearAgoFromToday();
    this.endDateShadowFeed = this.reports.getCurrentDate();
  }

  async ngOnInit(): Promise<void> {
    this.loadingThings = true;
    try {
      await this.view.init();
      await this.ensureDevicesLoaded();
      await this.loadClients();
      this.buildThingsFromDevices(this.iotService.devicesArray || []);
      if (this.view.selectedClient) {
        this.selectedClient = this.view.selectedClient;
        await this.getClientSubGroups();
        this.applyDeviceFilters();
      }
    } finally {
      this.loadingThings = false;
    }
  }

  private async ensureDevicesLoaded(): Promise<void> {
    if (!this.iotService.devicesArray || this.iotService.devicesArray.length === 0) {
      await this.iotService.adminDeviceArray();
    }
  }

  private async loadClients(): Promise<void> {
    await this.clientService.getClientsForSelect();
    this.thingClient = this.clientService.clientsArray || [];
  }

  private buildThingsFromDevices(devices: any[]): void {
    const seen = new Set<string>();
    this.things = (devices || [])
      .map((row: any) => ({
        id: row.thingName,
        name: row.thingName,
        device: row
      }))
      .filter((item) => {
        if (seen.has(item.id)) {
          return false;
        }
        seen.add(item.id);
        return true;
      });
  }

  onChangeAllThingsShadowFeed(event: any) {
    this.allThingsShadowFeed = (event.target as HTMLInputElement).checked;
    return this.allThingsShadowFeed;
  }

  async onClientChange() {
    this.filterThingGroup = '';
    this.filterThingSubGroup = '';
    this.view.selectedClient = this.selectedClient || '';
    this.view.saveFilters();
    await this.getClientSubGroups();
    this.applyDeviceFilters();
  }

  async onGroupChange() {
    this.filterThingSubGroup = '';
    this.applyDeviceFilters();
  }

  async onSubGroupChange() {
    this.applyDeviceFilters();
  }

  resetDeviceFilters() {
    this.selectedClient = '';
    this.filterThingGroup = '';
    this.filterThingSubGroup = '';
    this.selectedThingsShadowFeed = [];
    this.clientSubGroups = [];
    this.buildThingsFromDevices(this.iotService.devicesArray || []);
  }

  private async getClientSubGroups() {
    if (!this.selectedClient) {
      this.clientSubGroups = [];
      return;
    }
    try {
      const response: any = await firstValueFrom(
        this.iotService.getClientSubGroups(this.selectedClient)
      );
      const subgroups = Array.isArray(response) ? response : [];
      this.clientSubGroups = [...subgroups];
    } catch (err) {
      console.error('Failed to load subgroups', err);
      this.clientSubGroups = [];
    }
  }

  private applyDeviceFilters() {
    const baseDevices =
      this.iotService.originalDevicesArray?.length
        ? this.iotService.originalDevicesArray
        : this.iotService.devicesArray || [];
    let filtered = [...baseDevices];

    if (this.selectedClient) {
      filtered = filtered.filter((d: any) => d.bin?.client_id === this.selectedClient);
    }

    if (this.filterThingGroup) {
      filtered = filtered.filter(
        (d: any) => (d.thingName || '').toLowerCase().includes(this.filterThingGroup.toLowerCase())
      );
    }

    if (this.filterThingSubGroup) {
      const subgroup = this.clientSubGroups.find(
        (sg: any) => sg.sub_groupID === this.filterThingSubGroup
      );
      if (subgroup?.devices?.length) {
        const names = subgroup.devices.map((d: any) => d.thing_name);
        filtered = filtered.filter((d: any) => names.includes(d.thingName));
      }
    }

    this.buildThingsFromDevices(filtered);
  }

  async getShadowFeed() {
    this.loading = true;
    if (this.allThingsShadowFeed) {
      this.selectedThingsShadowFeed = this.things.map((thing: any) => thing.name);
    }

    const startDate = new Date(this.startDateShadowFeed);
    const timestampStartDateHour = startDate.setUTCHours(0, 0, 0);
    const timestampStartDate = Math.floor(timestampStartDateHour / 1000);

    const endDate = new Date(this.endDateShadowFeed);
    const timestampEndDateHour = endDate.setUTCHours(23, 59, 59);
    const timestampEndDate = Math.floor(timestampEndDateHour / 1000);

    try {
      const response = await this.iotService.getShadowFeed(
        this.selectedThingsShadowFeed,
        timestampStartDate,
        timestampEndDate
      );
      console.log('Shadow feed response', response);
      this.thingsShadowLog = response || [];
      this.loadedItemsShadowFeed = 0;
      this.thingsShadowLog.forEach((log: any) => {
        const newDate = new Date(Math.floor(log.timestamp));
        log.timestamp = this.iotService.formatDate(newDate);
      });
    } catch (err) {
      console.error('Shadow feed error', err);
    } finally {
      this.loading = false;
    }
  }

  @HostListener('window:scroll', [])
  onScroll(): void {
    const windowHeight =
      'innerHeight' in window
        ? window.innerHeight
        : document.documentElement.offsetHeight;
    const body = document.body;
    const html = document.documentElement;
    const documentHeight = Math.max(
      body.scrollHeight,
      body.offsetHeight,
      html.clientHeight,
      html.scrollHeight,
      html.offsetHeight
    );
    const scrollPosition =
      window.scrollY ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    this.showScrollTop = scrollPosition > 300;

    if (documentHeight - windowHeight - scrollPosition < 100) {
      this.loadMoreShadowFeedItems();
    }
  }

  scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private loadMoreShadowFeedItems(): void {
    if (
      this.loadedItemsShadowFeed + this.itemsPerPageShadowFeed <
      this.thingsShadowLog.length
    ) {
      this.loadedItemsShadowFeed += this.itemsPerPageShadowFeed;
    }
  }

  sortItemLog(itemSortBy: string) {
    if (this.sortBy === itemSortBy) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortDirection = 'asc';
    }

    this.sortBy = itemSortBy;

    this.thingsShadowLog.sort((a, b) => {
      switch (itemSortBy) {
        case 'thing_name':
          return this.sortDirection === 'asc'
            ? b.thing_name.localeCompare(a.thing_name)
            : a.thing_name.localeCompare(b.thing_name);
        case 'date':
          return this.sortDirection === 'asc'
            ? b.dst_timestamp - a.dst_timestamp
            : a.dst_timestamp - b.dst_timestamp;
        default:
          return a.thing_name.localeCompare(b.thing_name);
      }
    });
  }

  get thingGroupOptions() {
    return [
      { label: 'allGroups', value: '' },
      { label: 'DEV', value: 'dev' },
      { label: 'POC', value: 'poc' },
      { label: 'PRD', value: 'prd' },
    ];
  }
}
